import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, Clock, GraduationCap } from "lucide-react";
import CefrCard from "@/components/CefrCard";
import MockTestCard from "@/components/MockTestCard";

const STATUS_TONE = {
  pending: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
  confirmed: "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border-[hsl(var(--secondary))]/30",
  rescheduling: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30",
  finished: "bg-[hsl(var(--foreground))]/5 text-[hsl(var(--foreground))]/60 border-[hsl(var(--border))]",
  cancelled: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const STATUS_LABEL_EN = { pending: "Pending", confirmed: "Confirmed", rescheduling: "Rescheduling", finished: "Finished", cancelled: "Cancelled" };
const STATUS_LABEL_ZH = { pending: "待确认", confirmed: "已确认", rescheduling: "改期中", finished: "已完成", cancelled: "已取消" };

const TYPE_LABEL_EN = { interview: "Interview", trial_class: "Trial class", consultant: "Consultant call" };
const TYPE_LABEL_ZH = { interview: "面谈", trial_class: "试听课", consultant: "学习顾问" };

export default function PortalPage() {
  const { user, ready } = useAuth();
  const { t, lang } = useLang();
  const [announcements, setAnnouncements] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [myBookings, setMyBookings] = useState({ upcoming: [], past: [] });

  useEffect(() => {
    if (!user) return;
    (async () => {
      try { const { data } = await api.get(`/announcements?audience=students_parents`); setAnnouncements(data); } catch { /* ignore */ }
      try { const { data } = await api.get(`/schedules`); setSchedules(data.slice(0, 4)); } catch { /* ignore */ }
      try { const { data } = await api.get(`/bookings/me`); setMyBookings(data); } catch { /* ignore */ }
    })();
  }, [user]);

  if (!ready) return <div className="p-12">…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/portal" }} replace />;

  const statusLabel = (s) => (lang === "zh" ? STATUS_LABEL_ZH : STATUS_LABEL_EN)[s || "pending"] || s;
  const typeLabel = (s) => (lang === "zh" ? TYPE_LABEL_ZH : TYPE_LABEL_EN)[s] || s;

  return (
    <main data-testid="portal-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))]">DAWNVILLE · {t.portal.title.toUpperCase()}</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-3">
        {t.portal.welcome}, <span className="text-[hsl(var(--primary))]">{user.name || user.email}</span>
      </h1>

      {/* Coming Soon Events */}
      <section data-testid="portal-coming-soon" className="mt-12">
        <div className="flex items-center gap-2 mb-5">
          <CalendarIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
          <h2 className="font-display text-2xl tracking-tight">{t.portal.coming_soon}</h2>
          <span className="text-sm text-[hsl(var(--muted-foreground))]">· {myBookings.upcoming.length}</span>
        </div>
        {myBookings.upcoming.length === 0 ? (
          <div data-testid="portal-no-upcoming" className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-white p-8 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.portal.no_upcoming}</p>
            <Link to="/#booking" className="inline-block mt-3 text-sm text-[hsl(var(--primary))] hover:underline">{t.portal.book_now} →</Link>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-upcoming-list">
            {myBookings.upcoming.map((b) => (
              <li key={b.id} data-testid={`portal-upcoming-${b.id}`} className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 lift">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{typeLabel(b.booking_type)} · {b.program}</p>
                    <h3 className="font-display text-xl tracking-tight font-medium mt-1.5">{format(parseISO(b.preferred_date), "EEEE, MMM dd")}</h3>
                    <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{b.preferred_time}</p>
                    {b.notes && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic line-clamp-2">&quot;{b.notes}&quot;</p>}
                  </div>
                  <span data-testid={`portal-status-${b.id}`} className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${STATUS_TONE[b.status || "pending"]}`}>
                    {statusLabel(b.status)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {myBookings.past.length > 0 && (
          <details className="mt-6" data-testid="portal-past-details">
            <summary className="text-sm text-[hsl(var(--muted-foreground))] cursor-pointer hover:text-[hsl(var(--foreground))]">{t.portal.past_history} · {myBookings.past.length}</summary>
            <ul className="mt-4 divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
              {myBookings.past.map((b) => (
                <li key={b.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{b.preferred_date} · {b.preferred_time} · {typeLabel(b.booking_type)}</p>
                    <p className="text-sm">{b.program}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${STATUS_TONE[b.status || "finished"]}`}>{statusLabel(b.status)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <CefrCard />

      <div className="mt-8"><MockTestCard /></div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-7" data-testid="portal-announcements">
          <h2 className="font-display text-2xl tracking-tight mb-5">{t.portal.your_announcements}</h2>
          <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
            {announcements.map((a) => (
              <li key={a.id} className="p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{format(parseISO(a.created_at), "MMM dd, yyyy")}</p>
                <h4 className="font-display text-lg tracking-tight mt-1">{lang==="zh" && a.title_zh ? a.title_zh : a.title}</h4>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{lang==="zh" && a.content_zh ? a.content_zh : a.content}</p>
              </li>
            ))}
            {announcements.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">—</li>}
          </ul>
        </section>

        <section className="lg:col-span-5" data-testid="portal-schedules">
          <h2 className="font-display text-2xl tracking-tight mb-5"><GraduationCap className="inline w-5 h-5 mr-1 text-[hsl(var(--primary))]" />{t.portal.your_classes}</h2>
          <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl">
            {schedules.map((s) => (
              <li key={s.id} className="p-5 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{s.day_of_week} · {s.time_slot}</p>
                  <h4 className="font-display text-lg tracking-tight mt-1">{lang==="zh" && s.name_zh ? s.name_zh : s.name}</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">{s.teacher}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">{s.level}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
